import { useEffect, useState } from "react";
import Footer from "../../SharedModule/components/Footer";
import { Spinner } from "../../SharedModule/components/Spinner";
import { useUsers } from "../hooks/useUsers";
import { UserWithProfileType } from "../types/users-module-types";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import {
  ROUTE_USERS,
  STATUS_NOT_STARTED,
  STATUS_TERMINATED,
  USERS_ALL_ROLES,
} from "../../SharedModule/utils/constants";
import {
  changeOrder,
  resetFiltersUsers,
  SelectOrderCriteria,
  setActualUser,
  setFiltersUsers,
  setShowActives,
} from "../redux/reducers/usersList.reducer";
import { Select } from "../../SharedModule/components/Select";
import { useNavigate } from "react-router";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { getCostCenterChildrenIds } from "../../TimesModule/utils/timesUtils";
import { removeDiacritics } from "../utils/userUtils";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

const getEmploymentStatusSuffix = (employmentStatus: string): string => {
  if (employmentStatus === STATUS_NOT_STARTED) return " (!)";
  if (employmentStatus === STATUS_TERMINATED) return " (X)";
  return "";
};

export const UsersListPage = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const sortBy = useSelector(SelectOrderCriteria);

  useEffect(() => {
    document.title = "The Hub - Users";

    dispatch(setActiveTab(ROUTE_USERS));

    let posY = localStorage.getItem("userScrollY");
    if (posY)
      setTimeout(() => {
        window.scrollTo({
          top: Number(posY),
          behavior: "smooth",
        });
      }, 300);

    return () => {
      resetScrollY(globalThis.location.pathname);
      if (!globalThis.location.pathname.startsWith(ROUTE_USERS)) {
        dispatch(resetFiltersUsers());
        dispatch(
          changeOrder({
            orderBy: "fullName",
            dataType: "string",
            criteria: "asc",
          })
        );
      }
    };
  }, []);

  const {
    users,
    costCentersUsers,
    costCentersUsersComplete,
    isLoadingUsers,
    isShowingActives,
    selectFiltersUsers,
    userRolesList,
    rolesLoaded,
    supervisors,
  } = useUsers();

  let partialCostCentersId = [];
  selectFiltersUsers.costCenters.forEach((element) => {
    partialCostCentersId = partialCostCentersId.concat(
      getCostCenterChildrenIds(costCentersUsersComplete, element.value, "id")
    );
  });

  const [viewInactives, setViewInactives] = useState(!isShowingActives);
  const [searchText, setSearchText] = useState(selectFiltersUsers.searchText);
  const [filterCostCenter, setFilterCostCenter] = useState(
    selectFiltersUsers.costCenters
  );
  const [costCentersIdList, setCostCentersIdList] = useState<number[]>(
    Array.from(new Set(partialCostCentersId))
  );
  const [filterSupervisor, setFilterSupervisor] = useState(
    selectFiltersUsers.supervisors
  );
  const [supervisorsIdList, setSupervisorsIdList] = useState<string[]>(
    selectFiltersUsers.supervisors.map((item) => item.value)
  );

  let supervisorsActive = supervisors.filter(
    (supervisor) => supervisor.isActive
  );

  let costCenterToShow = [...costCentersUsers].sort((a: any, b: any) =>
    a.value < b.value ? -1 : 1
  );

  let userListToShow: Array<UserWithProfileType> = users
    .filter((user) => {
      return isShowingActives ? user.isActive : !user.isActive;
    })
    .filter((user: UserWithProfileType) =>
      removeDiacritics(user.fullName)
        .toLocaleLowerCase()
        .includes(removeDiacritics(searchText).toLocaleLowerCase())
    )
    .filter((user: UserWithProfileType) => {
      if (costCentersIdList.length > 0) {
        return (
          user.costCenter && costCentersIdList.includes(user.costCenter?.number)
        );
      } else {
        return user;
      }
    })
    .filter((user: UserWithProfileType) => {
      if (supervisorsIdList.length > 0) {
        return (
          user.userProfile?.supervisorId &&
          supervisorsIdList.includes(user.userProfile?.supervisorId)
        );
      } else {
        return user;
      }
    });

  const onToggleButton = (state: boolean) => {
    setViewInactives(state);
    dispatch(setShowActives(viewInactives));
  };

  const search = (value: string) => {
    setSearchText(value);
    dispatch(setFiltersUsers({ searchText: value }));
  };

  const goToProfile = (user: UserWithProfileType) => {
    localStorage.setItem("userScrollY", window.scrollY.toString());
    dispatch(
      setActualUser({
        actualUserData: user,
        allUsers: null,
        loadSupervisorName: false,
      })
    );

    navigate(`/users/${user.asuiteId}/profile`, {
      state: { user: user },
    });
  };

  const filterByDropdown = (costCenter, supervisor) => {
    if (costCenter) {
      setFilterCostCenter(costCenter);
      dispatch(setFiltersUsers({ costCenters: costCenter }));
      partialCostCentersId = [];
      costCenter.forEach((element) => {
        partialCostCentersId = partialCostCentersId.concat(
          getCostCenterChildrenIds(
            costCentersUsersComplete,
            element.value,
            "id"
          )
        );
      });
      setCostCentersIdList(Array.from(new Set(partialCostCentersId)));
    }
    if (supervisor) {
      setFilterSupervisor(supervisor);
      dispatch(setFiltersUsers({ supervisors: supervisor }));
      setSupervisorsIdList(supervisor.map((item) => item.value));
    }
  };

  return (
    <>
      {(isLoadingUsers || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {!isLoadingUsers &&
        rolesLoaded &&
        !verifyModuleAccess(true, USERS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {!isLoadingUsers &&
        rolesLoaded &&
        verifyModuleAccess(true, USERS_ALL_ROLES, userRolesList) && (
          <div className="content">
            <div
              className="content-header"
              style={{ marginTop: "0", marginBottom: "1rem" }}
            >
              <h2>Users</h2>
            </div>

            <div className="d-flex mb-3 justify-content-between">
              <div className="flex-column col-sm-3">
                <label htmlFor="users-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="users-search"
                  type="text"
                  className="form-control form-control-sm common-input"
                  placeholder="Search by first name, last name, or both"
                  value={searchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>
              <div className="flex-column col-sm-4">
                <label htmlFor="users-cost-centers" className="col-form-label">
                  Cost Centers
                </label>
                <Select
                  inputId="users-cost-centers"
                  isMulti
                  placeholder="All Cost Centers"
                  options={costCenterToShow}
                  value={filterCostCenter}
                  onChange={(costCenter) => filterByDropdown(costCenter, null)}
                  isDisabled={isLoadingUsers}
                />
              </div>
              <div className="flex-column col-sm-2">
                <label htmlFor="users-supervisor" className="col-form-label">
                  Supervisor
                </label>
                <Select
                  inputId="users-supervisor"
                  isMulti
                  placeholder="All"
                  options={supervisorsActive}
                  value={filterSupervisor}
                  onChange={(supervisor) => filterByDropdown(null, supervisor)}
                  isDisabled={isLoadingUsers}
                />
              </div>
              <fieldset className="flex-column col-sm-2">
                <div className="col-form-label">Profile Status</div>
                <div className="btn-group w-100">
                  <button
                    type="button"
                    onClick={() => onToggleButton(false)}
                    className={`btn btn-group-custom ${
                      viewInactives ? "btn-light" : "btn-active"
                    }`}
                    disabled={!viewInactives || isLoadingUsers}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleButton(true)}
                    className={`btn btn-group-custom ${
                      viewInactives ? "btn-inactive" : "btn-light"
                    }`}
                    disabled={viewInactives || isLoadingUsers}
                  >
                    Inactive
                  </button>
                </div>
              </fieldset>
            </div>

            {!isLoadingUsers && userListToShow.length === 0 && (
              <div className="py-5">
                <NoContent
                  title="No Results"
                  text="Edit your filters to find the users you are looking for."
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {!isLoadingUsers && userListToShow.length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-7"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-15 pointer ${
                        sortBy.orderBy === "fullName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="fullName"
                      id="fullName"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "fullName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Name
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-15 pointer ${
                        sortBy.orderBy === "jobTitle"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="jobTitle"
                      id="jobTitle"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "jobTitle",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Job Title
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-15 pointer ${
                        sortBy.orderBy === "priceLevel"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="priceLevel"
                      id="priceLevel"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "priceLevel",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Price Level
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-15 pointer ${
                        sortBy.orderBy === "costCenter"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="costCenter"
                      id="costCenter"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "costCenter",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      Cost Center
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-15 pointer ${
                        sortBy.orderBy === "supervisorName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="supervisorName"
                      id="supervisorName"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "supervisorName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Supervisor
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-25 pointer ${
                        sortBy.orderBy === "employmentStatus"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="employmentStatus"
                      id="employmentStatus"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "employmentStatus",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Employment Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userListToShow.map((user: UserWithProfileType) => {
                    return (
                      <tr
                        key={user.id}
                        className="product-link"
                        onClick={() => goToProfile(user)}
                      >
                        <td
                          className={`ps-5 fw-500 td-shadow-effect ${
                            user.userProfile?.employmentStatus ===
                              STATUS_NOT_STARTED ||
                            user.userProfile?.employmentStatus ===
                              STATUS_TERMINATED
                              ? "fst-italic"
                              : ""
                          }`}
                        >
                          {user.fullName}
                          {getEmploymentStatusSuffix(
                            user.userProfile?.employmentStatus
                          )}
                        </td>
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {user.userProfile?.jobTitle}
                        </td>
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {user.userProfile?.priceLevel}
                        </td>
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {user.costCenter
                            ? `${user.costCenter.number} - ${
                                user.costCenter.name
                              } ${!user.costCenter.isActive ? "(X)" : ""}`
                            : ""}
                        </td>
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {user.userProfile?.supervisorName}
                        </td>
                        <td
                          className={`ps-5 fw-500 td-shadow-effect ${
                            user.userProfile?.employmentStatus ===
                              STATUS_NOT_STARTED ||
                            user.userProfile?.employmentStatus ===
                              STATUS_TERMINATED
                              ? "fst-italic"
                              : ""
                          }`}
                        >
                          {user.userProfile?.employmentStatus}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <Footer />
          </div>
        )}
    </>
  );
};
